/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { Component, Fragment } from 'react';
import ReactDOMServer from 'react-dom/server';
import { Button, UncontrolledCollapse } from 'reactstrap';
import Loader from '../../loader';
import { loadSurveyCalculationView } from '../../../actions/survey'
import NumberFormat from '../../../utils/number-format';

class CollapseCard extends Component {

  id = Math.random().toString(36).substr(2, 9);
  static defaultProps = {
    index: 0,
  }

  render = () => {
    const { config, index } = this.props;
    if (!config) {
      return 'blank';
    }

    if (Array.isArray(config)) {
      return config.map((c, i) => <CollapseCard key={Math.random()} config={c} index={index + i} />);
    }

    const renderDataRow = (variableName, title, value) => {
      let friendlyValue = convertValue(value);
      const html = {
        __html: `<span class='variableTitle'>${variableName}.</span>  ${title}`
      };
      return <Fragment>
        <div className='flex-grow-1' dangerouslySetInnerHTML={html} />
        <div className='text-right'>{friendlyValue}</div>
      </Fragment>
    }

    const { title, value, letter, children } = config;
    const variableName = letter;

    if (!children) {
      return <div className='value-calculation-card'>
        <Button disabled outline color='secondary' className='d-flex pl-3'>
          {renderDataRow(variableName, title, value)}
        </Button>
      </div>
    }

    const togglerId = `calc-toggler-${this.id}`;
    return <div className='value-calculation-card'>
      <Button outline color='secondary' id={togglerId} className='d-flex'>
        <i className='fa fa-chevron-down' />
        {renderDataRow(variableName, title, value)}
      </Button>
      <UncontrolledCollapse toggler={`#${togglerId}`} className='ml-3'>
        <CollapseCard config={children} index={0} />
      </UncontrolledCollapse>
    </div>
  }
}

const isNumeric = n => !isNaN(parseFloat(n)) && isFinite(n);

const convertValue = (value) => {
  switch (value) {
    case 'GOAL_SUM':
      return 'SUM';
    case 'CREATED':
      return <span className='variableWarning' title={'Variable has not been submitted'}>
        <i className='fa fa-exclamation-triangle' />
      </span>;
    case 'NO_DATA_AVAILABLE':
      return <span className='noData' title={'Variable is not in scope or is deleted'}>
        <i className='fa fa-ban' />
      </span>
    case '{}':
      return <span className='partial text-right' title={'Variable has been partially submitted'}>
        <i className='fa fa-exclamation-triangle' />
      </span>
    case 'NA':
      return <span className='variableWarning'>N/A</span>;
    case 'NR':
      return <span className='variableWarning'>N/R</span>;
    default:
      return isNumeric(value) ? <NumberFormat value={value} /> : value;
  }
}

class ValueCalculation extends Component {

  state = {
    data: undefined,
    loaded: false,
    error: false,
    errorMessage: '',
  }

  componentDidMount() {
    loadSurveyCalculationView(this.props.surveyId)
      .then((data) => this.setState({ data, loaded: true }))
      .catch(e => this.setState({ error: true, errorMessage: e.message }));
  }

  render() {
    const { loaded, error, errorMessage } = this.state;
    const { utrCode } = this.props;

    if (error) {
      return <div>{errorMessage}</div>;
    }

    if (!loaded) {
      return <Loader />
    }

    const config = this.getDataByCode(utrCode);
    return <div className='valueCalculationContainer'>
      <CollapseCard config={config} />
    </div>
  }

  getTitle = (title, letter) => {
    const html = (dom) => ReactDOMServer.renderToString(dom);
    const icon = (icon) => <i className={`fa ${icon} px-1`} />;
    const warning = (text) => <span className={'variableWarning'}>{text}</span>;
    title = title.replace(/ /g, '');
    title = title.replace(/\(/g, ' ( ');
    title = title.replace(/\)/g, ' ) ');
    title = title.replace(/\//g, ' / ');
    title = title.replace(/-/g, ' - ');
    title = title.replace(/\+/g, ' + ');
    title = title.replace(/\*/g, ' * ');
    title = title.replace(/=/g, html(icon('fa-equals')));
    title = title.replace(/CREATED/g, html(warning(icon('fa-exclamation-triangle'))));
    title = title.replace(/NO_DATA_AVAILABLE/g, html(convertValue('NO_DATA_AVAILABLE')));
    title = title.replace(/NA/g, html(warning('N/A')));
    title = title.replace(/NR/g, html(warning('N/R')));
    title = title.replace(/ \/ /g, html(icon('fa-divide')));
    title = title.replace(/ - /g, html(icon('fa-minus')));
    title = title.replace(/ \+ /g, html(icon('fa-plus')));
    title = title.replace(/ \* /g, html(icon('fa-times')));
    title = title.replace(/{/g, `<span class='variable'>${letter}`);
    title = title.replace(/}/g, '</span>');
    return title;
  }

  getCardItems = (item, currentLetter, variableLetterPrefix) => {
    return Object.keys(item).map((k) => ({
      title: this.getTitle(item[k], variableLetterPrefix),
      letter: currentLetter
    }));
  }

  getDataByCode = (code, option, calculatedValue, letter = 'a') => {
    const config = this.getConfig(code)

    if (!config) {
      return {
        title: `${code}${option ? ` (${option})` : ''}`,
        letter: letter,
        value: calculatedValue
      };
    }

    const { calculated, calculation } = config.importConfigurationData
    const calculationKeys = Object.keys(calculation);
    const configurationKeys = Object.keys(config.importConfigurationData);
    const variableKeys = configurationKeys.filter(k => !['calculated', 'calculation'].includes(k));
    const variables = variableKeys.map(k => ({ letter: k, ...config.importConfigurationData[k] }));

    const alphabet = 'abcdefghijklmnopqrstuvwxyz'.split('');

    const viewConfigs = [];
    const data = {
      title: code,
      letter: letter,
      value: calculated,
      children: [],
    }

    if (calculationKeys.length === 1) {
      const key = calculationKeys[0];
      const [, type] = key.split('-');
      const actionItems = calculation[key];
      const title = this.getActionTypeTitle(type, actionItems, alphabet)
      data.title = `${code} - ${this.getTitle(title, letter)}`
    } else {
      data.title = `${code} - Multiple Options`
    }

    const finalLetter = alphabet[0];

    calculationKeys.forEach(k => {
      const actionItems = calculation[k];
      actionItems.forEach((item, i) => {
        const items = this.getCardItems(item, `${letter}${alphabet[i]}`, `${letter}${finalLetter}`);
        items.forEach(i => data.children.push(i));
      });
    });

    // Add variables at the end
    variables.forEach(v => {
      const childLetter = `${letter}${finalLetter}${v.letter}`
      data.children.push(this.getDataByCode(v.code, v.option, v.value, childLetter))
    })
    viewConfigs.push(data);
    return viewConfigs;
  }

  getActionTypeTitle(type, actionItems, alphabet) {
    const actionType = type.toUpperCase();
    if (actionType === 'GOAL_SUM') {
      let title = '('
      actionItems.forEach((a, i) => title = `${title} ${i > 0 ? '+' : ''} {${alphabet[i]}} `)
      return `${title})`;
    }

    return convertValue(actionType)
  }

  getConfig(code) {
    const { resolvedConfigs, referencedBy } = this.state.data;
    const config = resolvedConfigs.find(c => c.code === code);
    if (config) {
      return config
    }

    if (Array.isArray(referencedBy)) {
      for (const refData of referencedBy) {
        const refConfig = refData.resolvedConfigs.find(c => c.code === code);
        if (refConfig) {
          return refConfig
        }
      }
    }
  }
}

export default ValueCalculation;
